import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { I18nextProvider } from 'react-i18next';
import { App } from './App';
import { i18n } from '@lib/i18n';
import { initSentry, Sentry } from '@lib/sentry';
import { getStoredConsent, initPostHog } from '@lib/posthog';
import { hasPersistedSupabaseSession } from '@lib/supabase';
import { yeniSurumYenilemesiniKur, yenilemeSuruyor } from '@lib/yeniSurum';
import './index.css';

// Deploy sonrası bayat sekme: eski chunk 404 verirse sayfayı bir kez yenile (ayrıntı lib/yeniSurum.ts).
// Aşağıdaki prefetch importlarından ÖNCE kurulmalı.
yeniSurumYenilemesiniKur();

// EARLY route prefetch — main bundle parse'i ile PARALEL chunk indir.
// Session varsa kullanici authenticated demek → ilk gidecegi yer dashboard
// veya hot route'lardan biri. Bu sayede login'den sonra menuye tikladiginda
// chunk zaten cache'de olur. Anonim ziyaretci icin App.tsx useRoutePrefetch
// devreye girer (login sonrasi).
if (hasPersistedSupabaseSession()) {
  void import('@features/dashboard/DashboardPage');
  void import('@features/musteri/MusteriListPage');
  void import('@features/firsat/FirsatKanbanPage');
  void import('@features/aktivite/AktivitePage');
  void import('@features/takvim/TakvimPage');
  void import('@features/urun/UrunListPage');
  void import('@features/teklif/TeklifListPage');
  void import('@features/siparis/SiparisListPage');
  void import('@features/destek/TalepListPage');
  void import('@features/ayarlar/AyarlarPage');
}

// Non-critical font weight'leri idle'da yukle (render-blocking degil).
// Inter 400 zaten index.css'te critical. Bu chunk'lar ayri lazy module'ler.
function loadDeferredFonts() {
  void import('@fontsource/inter/500.css');
  void import('@fontsource/inter/600.css');
  void import('@fontsource/inter/700.css');
  void import('@fontsource/jetbrains-mono/400.css');
  void import('@fontsource/jetbrains-mono/500.css');
}
if (typeof window.requestIdleCallback === 'function') {
  window.requestIdleCallback(loadDeferredFonts, { timeout: 3000 });
} else {
  setTimeout(loadDeferredFonts, 1500);
}

// PWA Service Worker registration — agresif sekilde geciktirilir.
// Default vite-plugin-pwa register'i window.load'da fire eder, ama workbox
// precache (~10 dosya/600KB) main thread'i tikar (desktop'ta TBT 1sn olur).
// 4 saniye gecikme + idle callback ile TTI'dan sonra registrasyon yapar.
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  const registerSW = () => {
    void import('virtual:pwa-register').then(({ registerSW: register }) => {
      register({ immediate: false });
    });
  };
  window.addEventListener('load', () => {
    setTimeout(() => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(registerSW, { timeout: 5000 });
      } else {
        registerSW();
      }
    }, 4000);
  });
}

initSentry();

// PostHog **dynamic import** ile lazy yüklenir (eager bundle'dan çıkarmak için).
// Sadece kullanıcı daha önce onay vermişse şimdi yükle; vermemişse banner'da
// "Kabul Et" basınca yüklenecek. Bu, anonim ilk ziyaretçi için ~150 KB tasarruf.
if (getStoredConsent() === 'granted') {
  // requestIdleCallback ile boş zamanda yükle — ilk paint'i bloklamasın
  const loadPostHog = () => void initPostHog();
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(loadPostHog, { timeout: 2000 });
  } else {
    setTimeout(loadPostHog, 0);
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function ErrorFallback() {
  // Yeni sürüm için yenileme başladıysa hata ekranını gösterme; sayfa birazdan yeniden yüklenir.
  if (yenilemeSuruyor()) return null;
  return (
    <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'system-ui' }}>
      <h1>Beklenmedik bir hata oluştu</h1>
      <p>Lütfen sayfayı yenileyin. Sorun devam ederse destek ekibine bildirin.</p>
      <button onClick={() => window.location.reload()} style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}>
        Sayfayı yenile
      </button>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <App />
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
      </I18nextProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
